import { isTextModel } from "@/constants/liveModels";
import { type AgentTool, SAVED_SECRET, type UpdateCustomToolBody } from "@/services/tool.service";

// Per-tool Gemini tool-calling options for custom (HTTP) tools, stored on
// user_tools as tool_behavior / result_scheduling. Native tools never take them.
// Plan: pipecat/reports/tool-calling-plan-2026-09-19.md §3.5.

/** The values the database allows; tool-options.test.ts checks them against shared/tool-options.json. */
export const TOOL_BEHAVIORS = ["blocking", "non_blocking"] as const;
export const RESULT_SCHEDULINGS = ["when_idle", "silent"] as const;

export type ToolBehavior = (typeof TOOL_BEHAVIORS)[number];
export type ResultScheduling = (typeof RESULT_SCHEDULINGS)[number];

/** Which bot runs the agent: 3.8 Live (the options apply) or a text model (they are ignored). */
export type LiveRuntime = "live38" | "text";

/** The dialog's form values. */
export interface ToolOptions {
  toolBehavior: ToolBehavior;
  resultScheduling: ResultScheduling;
}

/** The two columns as written. "Wait for the result" is stored as NULL, the platform default. */
export interface ToolOptionColumns {
  tool_behavior: ToolBehavior | null;
  result_scheduling: ResultScheduling | null;
}

export const DEFAULT_TOOL_OPTIONS: ToolOptions = { toolBehavior: "blocking", resultScheduling: "when_idle" };

export const TOOL_BEHAVIOR_OPTIONS: ReadonlyArray<{ value: ToolBehavior; label: string; help: string }> = [
  {
    value: "blocking",
    label: "Wait for the result",
    help: "The agent pauses until the tool answers, then gives the caller the result. Best for quick lookups the caller is waiting for.",
  },
  {
    value: "non_blocking",
    label: "Keep talking",
    help: "The agent stays free while the tool runs: if the caller says something, it answers. It does not fill the silence on its own. Best for slow tools.",
  },
];

/** "Don't say it" (plan §9, D5, decided by the Phase 0 measurements): the bot acknowledges the call at once
 * and adds the real result to the conversation silently when it arrives. Change the copy here only. */
export const SILENT_RESULT_HELP = "The agent confirms the request right away without waiting for the tool, and never announces the result, but can use it if the caller asks later. For tools that only save or send something.";

export const RESULT_SCHEDULING_OPTIONS: ReadonlyArray<{ value: ResultScheduling; label: string; help: string }> = [
  {
    value: "when_idle",
    label: "Say it when the agent has finished talking",
    help: "The agent finishes what it is saying, then gives the result.",
  },
  { value: "silent", label: "Don't say it", help: SILENT_RESULT_HELP },
];

export const TOOL_OPTIONS_NOTE: Record<LiveRuntime, string> = {
  live38: "Applies on Gemini 3.8 Live.",
  text: "This agent runs on a text model, which waits for every tool. These settings apply on Gemini 3.8 Live.",
};

/** pipecat/shared/agent_tools.py NATIVE_TOOL_NAMES: a custom tool named like one takes no options. */
export const PLATFORM_TOOL_NAMES = ["end_call", "report_voicemail", "send_sms", "transfer_call"] as const;

export const PLATFORM_NAME_NOTE =
  "A tool named like a platform tool (end_call, report_voicemail, send_sms, transfer_call) takes none of these settings. Rename it to use them.";

/** Whether the dialog's options are locked, and the note it shows: the runtime's, or the name's. */
export const toolOptionsState = (runtime: LiveRuntime, toolName?: string): { locked: boolean; note: string } =>
  (PLATFORM_TOOL_NAMES as readonly string[]).includes((toolName ?? "").trim())
    ? { locked: true, note: PLATFORM_NAME_NOTE }
    : { locked: runtime !== "live38", note: TOOL_OPTIONS_NOTE[runtime] };

const oneOf = <T extends string>(value: unknown, allowed: readonly T[]): T | null =>
  (allowed as readonly unknown[]).includes(value) ? (value as T) : null;

/**
 * The bot an agent's calls run on, as pipecat/bot.py picks it: the model id
 * compared exactly (one of the text models, anything else 3.8 Live: the 3.1 bot
 * is gone), and no thinking level (Extended Thinking is switched off there, so a
 * 3.8 agent runs on 3.8 Live at any stored level).
 */
export const liveRuntime = (model: unknown): LiveRuntime => (isTextModel(model) ? "text" : "live38");

/** A stored row as the dialog shows it: anything but non_blocking is Wait; an unknown scheduling is when_idle. */
export const toolOptionsFromRow = (row: { tool_behavior?: unknown; result_scheduling?: unknown } | null | undefined): ToolOptions => ({
  toolBehavior: row?.tool_behavior === "non_blocking" ? "non_blocking" : "blocking",
  resultScheduling: oneOf(row?.result_scheduling, RESULT_SCHEDULINGS) ?? "when_idle",
});

/** What a custom-tool save writes: always both keys, so leaving Keep talking clears the scheduling too. */
export const toolOptionColumns = ({ toolBehavior, resultScheduling }: ToolOptions): ToolOptionColumns =>
  toolBehavior === "non_blocking"
    ? { tool_behavior: "non_blocking", result_scheduling: oneOf(resultScheduling, RESULT_SCHEDULINGS) ?? "when_idle" }
    : { tool_behavior: null, result_scheduling: null };

/**
 * The options a row or an export entry carries, through the closed sets: an
 * unknown value becomes null, a scheduling needs non_blocking, and a native
 * tool carries none. It never refuses, so an import never drops a tool.
 */
export const storedOptionColumns = (tool: { is_native?: unknown; tool_behavior?: unknown; result_scheduling?: unknown }): ToolOptionColumns => {
  const behavior = tool.is_native ? null : oneOf(tool.tool_behavior, TOOL_BEHAVIORS);
  return {
    tool_behavior: behavior,
    result_scheduling: behavior === "non_blocking" ? oneOf(tool.result_scheduling, RESULT_SCHEDULINGS) : null,
  };
};

/**
 * One header row of the edit dialog. A row the tool has saved is `saved`: its
 * value is never shown (the function answers it as SAVED_SECRET), so the input
 * starts empty, and `originalKey` is the name the value is saved under.
 */
export interface HeaderRow {
  key: string;
  value: string;
  saved: boolean;
  originalKey: string;
}

export const emptyHeaderRow = (): HeaderRow => ({ key: "", value: "", saved: false, originalKey: "" });

/** EditToolDialog's custom-tool fields, for both its useState initialisers and its on-open effect. */
export const editDialogInitialState = (tool: AgentTool) => {
  // A value that is not the placeholder came from a function that did not mask
  // it yet: it is shown, as before.
  const headers = Object.entries(tool.headers || {}).map(([key, value]): HeaderRow =>
    value === SAVED_SECRET
      ? { key, value: "", saved: true, originalKey: key }
      : { key, value: String(value), saved: false, originalKey: key },
  );
  return {
    toolName: tool.tool_name,
    description: tool.description || "",
    endpointUrl: tool.endpoint_url,
    httpMethod: tool.http_method,
    headers: headers.length > 0 ? headers : [emptyHeaderRow()],
    parametersJson: tool.parameters ? JSON.stringify(tool.parameters, null, 2) : "",
    timeoutSeconds: tool.timeout_seconds || 30,
    toolOptions: toolOptionsFromRow(tool),
  };
};

/**
 * The update EditToolDialog saves for a custom tool, from its validated fields.
 * Both option keys always: the function writes a null, so "Wait for the result"
 * clears a stored "Keep talking".
 */
export const customToolUpdates = (fields: {
  toolName: string;
  description: string;
  endpointUrl: string;
  httpMethod: string;
  headers: Record<string, string>;
  parameters: AgentTool["parameters"];
  timeoutSeconds: number;
  toolOptions: ToolOptions;
}): UpdateCustomToolBody => ({
  tool_name: fields.toolName,
  description: fields.description || null,
  endpoint_url: fields.endpointUrl,
  http_method: fields.httpMethod,
  headers: fields.headers,
  parameters: fields.parameters,
  timeout_seconds: fields.timeoutSeconds,
  ...toolOptionColumns(fields.toolOptions),
});

/**
 * The headers an edit sends: each row with a name, and a saved row left empty
 * under its own name as SAVED_SECRET, which keeps the value saved there. A row
 * the user removed is simply not sent, and the function drops it.
 */
export const headerValues = (rows: HeaderRow[]): Record<string, string> =>
  Object.fromEntries(
    rows
      .filter((row) => row.key.trim())
      .map((row) => [row.key, row.saved && !row.value && row.key === row.originalKey ? SAVED_SECRET : row.value]),
  );

/**
 * An export entry's headers as an import sends them. A file exported since the
 * function stopped answering secrets holds SAVED_SECRET for every value, which a
 * new tool refuses: those headers are left out and named, for the user to type
 * again. An older file's real values go through as they are.
 */
export const importedHeaders = (headers: AgentTool["headers"]) => {
  const entries = Object.entries(headers ?? {});
  const masked = entries.filter(([, value]) => value === SAVED_SECRET).map(([name]) => name);
  return {
    headers: masked.length > 0 ? Object.fromEntries(entries.filter(([, value]) => value !== SAVED_SECRET)) : headers,
    masked,
  };
};

/** One tools[] entry of an agent export (v2.0): the tool's configuration, no ids.
 * Its header values are the placeholder the function answers, so a file never
 * holds a secret. */
export const exportedTool = (tool: AgentTool) => ({
  tool_name: tool.tool_name,
  description: tool.description,
  endpoint_url: tool.endpoint_url,
  http_method: tool.http_method,
  headers: tool.headers,
  parameters: tool.parameters,
  timeout_seconds: tool.timeout_seconds,
  is_native: tool.is_native,
  sms_content_type: tool.sms_content_type,
  sms_static_message: tool.sms_static_message,
  transfer_call_mode: tool.transfer_call_mode,
  transfer_call_destination: tool.transfer_call_destination,
  transfer_call_type: tool.transfer_call_type,
  ...storedOptionColumns(tool),
});

/**
 * The error a tool dialog shows when a save fails. The `tool` function's refusal
 * of the options (a 422 naming one of them) gets words the user can act on;
 * anything else keeps its own message.
 */
export const toolSaveErrorMessage = (error: unknown): string => {
  const { status, message } = (error ?? {}) as { status?: unknown; message?: unknown };
  const text = typeof message === "string" ? message : "";
  if (status === 422 && /^(tool_behavior|result_scheduling):/.test(text)) {
    return 'The tool was not saved: its "While this tool runs" settings were refused. Choose "Wait for the result", or "Keep talking" and when to give the result, then save again.';
  }
  return text || "The tool could not be saved.";
};
